// Catalog navigation on the public asset pages swaps the results region in
// place instead of doing a Turbolinks visit.
//
// Why: a Turbolinks visit replaces the whole <body>, and the destination
// document is a different height, so the browser clamps the scroll to 0 and
// paints a frame at the top of the page before anything can put it back. That
// frame flashed the promo banner into view and read as a jump. Swapping only
// #assets-results leaves the banner, the header and the scroll position
// completely untouched — and it is faster, because the server skips the
// page shell and the popular-libraries rail on this path.
//
// The response is the same partial the full page renders, so the sidebar's
// counts and active state, the result count and the grid all refresh together
// with no client-side state to keep in step.
//
// Progressive enhancement: the links stay real hrefs. With JS off, or if the
// fetch fails, they navigate normally.

const CONTAINER = "#assets-results"
const STATUS = "#assets-results-status"

// Marks our own history entries so popstate can tell them apart from
// Turbolinks', which owns the entry for the page we arrived on.
const STATE_KEY = "assetsResultsSwap"

let sequence = 0

// Catalog links and the toolbar's filter chips: every link that only changes
// a facet. The toolbar's type menu is handled separately below.
const LINKS = ".asset-browse-catalog-link, .js-assets-filter-link"

function container() {
  return document.querySelector(CONTAINER)
}

function announce($container) {
  const $count = $container.find(".js-assets-count").first()
  const count = $count.text().trim()
  if (!count) return

  // Written into a live region that lives outside the swapped markup: a
  // freshly inserted aria-live element is not reliably announced.
  // data-count-noun names what was counted (the libraries page counts
  // libraries, not assets).
  $(STATUS).text(count + " " + ($count.data("countNoun") || "assets"))
}

// Focus follows the click. The link that was activated has just been replaced,
// so without this focus falls to <body> and keyboard users lose their place in
// the tree.
function refocus($container, url, focus) {
  if (focus) {
    $container.find(focus).first().trigger("focus")
    return
  }
  const path = url.replace(window.location.origin, "")
  const match = $container.find(LINKS).filter(function () {
    return this.getAttribute("href") === path
  })

  // The link just clicked may not exist any more (a chip removes itself);
  // fall back to the active node.
  const target = match.length
    ? match
    : $container.find(".asset-browse-catalog-link.active")
  if (target.length) target.first().trigger("focus")
}

function swap(url, { push, focus }) {
  const mine = ++sequence
  const $container = $(container())

  $container.attr("aria-busy", "true")

  return $.ajax({ method: "GET", url: url, dataType: "json" })
    .done(function (response) {
      // A newer click won the race; painting this one would show stale facets.
      if (mine !== sequence) return

      // jQuery .html() evaluates inserted <script> tags, which is what applies
      // the stored card size and sidebar section state (the boot scripts in
      // assets/_browse and assets/_sidebar) before the new markup paints.
      $container.html(response.html).attr("aria-busy", "false")

      if (push) window.history.pushState({ [STATE_KEY]: true }, "", url)

      announce($container)
      refocus($container, url, focus)
    })
    .fail(function () {
      // A superseded request failing late must not undo the newer selection.
      if (mine !== sequence) return

      // Give up on the enhancement rather than leaving a stale page.
      window.location.href = url
    })
}

$(document).on(
  "click",
  CONTAINER +
    " .asset-browse-catalog-link, " +
    CONTAINER +
    " .js-assets-filter-link",
  function (event) {
    // A modified click is the user asking for a new tab or window.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    if (event.which && event.which !== 1) return
    if (!container() || !window.history.pushState) return

    event.preventDefault()
    swap(this.getAttribute("href"), { push: true })
  }
)

// The toolbar's "Type of Asset" menu. Each option's value is the browse href
// for that type, so a change is the same swap a facet link click does.
$(document).on("change", CONTAINER + " .js-assets-type-select", function () {
  const url = this.value
  if (!window.history.pushState) {
    window.location.href = url
    return
  }
  // The menu is replaced with the region; keep focus on its replacement.
  swap(url, { push: true, focus: ".js-assets-type-select" })
})

// Back/forward. Only our own entries are handled here — an entry Turbolinks
// pushed (arriving on the page, or navigating away) carries its own state and
// is left to Turbolinks, which restores its snapshot.
$(window).on("popstate", function (event) {
  const state = event.originalEvent && event.originalEvent.state
  if (!state || !state[STATE_KEY]) return
  if (!container()) return

  swap(window.location.href, { push: false })
})
